/**
 * Download one of a report's datasets through `POST /api/reports/{slug}/export`.
 *
 * The file is built server-side from the full result (§9.4), never from what
 * the page rendered, so a paged or virtualised table still exports every row.
 * Pass the run's own `result.params` -- the coerced values that produced the
 * screen -- so the file holds exactly the rows the reader is looking at.
 *
 * The route is CSRF-exempt (it changes no state), and the session cookie goes
 * with a same-origin fetch, so this needs nothing from `lib/api`'s JSON path.
 */

export type ExportFormat = 'csv' | 'xlsx'

export async function exportDataset(
  slug: string,
  params: Record<string, unknown>,
  dataset: string,
  format: ExportFormat,
): Promise<void> {
  const response = await fetch(`/api/reports/${encodeURIComponent(slug)}/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ params, dataset, format }),
  })
  if (!response.ok) {
    throw new Error(`Export failed: ${String(response.status)} ${response.statusText}`)
  }
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const named = /filename="([^"]+)"/.exec(disposition)?.[1]
  const filename = named ?? `${slug}-${dataset}.${format}`
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
